import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import ChatWidget from '../components/ChatWidget';
import { ChatProvider } from '../contexts/ChatContext';
import { LanguageProvider, SupportedLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import { UserType } from '../types';
import {
  JFC_CHAT_EVENTS,
  SyncUserPayload,
  LogoutUserPayload,
  ChangeLanguagePayload,
  dispatchWidgetReady
} from '../lib/eventBridge';
import '../app/globals.css';
import packageJson from '../../package.json';

console.log('[JFCChatWidget] version', packageJson.version);
function toUserType(rawType: string): UserType {
  const normalized = rawType?.toLowerCase();
  if (normalized === 'salesrep' || normalized === 'sale') {
    return UserType.SalesRep;
  }
  return UserType.Customer;
}

function getAccountKey(userID: string | undefined, email: string | undefined): string {
  const normalizedEmail = email?.trim().toLowerCase();
  return normalizedEmail || userID?.trim() || '';
}

function getChatSessionCleanup(): (() => Promise<void>) | null {
  const cleanup = (window as any).__jfcChatLogout;
  return typeof cleanup === 'function' ? cleanup : null;
}

export interface JFCChatWidgetConfig {
  userID: string;
  jfcToken: string;
  userInfo: {
    type: UserType;
    nickname: string;
    faceURL?: string;
    email?: string;
    phone?: string;
  };
  groupID?: string;       // OpenIM groupID = customerId (e.g. "000635")
  groupName?: string;
  customerNo?: string;    // Customer number (e.g. "14-0000") for display only
  mode?: 'widget' | 'fullscreen';
  containerId?: string;
  triggerButtonId?: string;
  language?: SupportedLanguage;
}

class JFCChatWidget {
  private root: Root | null = null;
  private container: HTMLElement | null = null;
  private config: JFCChatWidgetConfig;
  private eventListeners: { [key: string]: EventListener } = {};

  constructor(config: JFCChatWidgetConfig) {
    this.config = {
      mode: 'widget',
      containerId: 'jfc-chat-widget-container',
      ...config,
    };
  }

  /**
   * Initialize and render the chat widget
   */
  init() {
    // Create container if it doesn't exist
    let container = document.getElementById(this.config.containerId!);
    if (!container) {
      container = document.createElement('div');
      container.id = this.config.containerId!;
      document.body.appendChild(container);
    }
    this.container = container;

    // Create React root and render
    this.root = createRoot(container);
    this.render();

    // Setup trigger button if specified
    if (this.config.triggerButtonId) {
      this.setupTriggerButton();
    }

    // Setup event listeners for host app communication
    this.setupEventListeners();

    // Notify host app that widget is ready
    dispatchWidgetReady();

    return this;
  }

  /**
   * Setup event listeners for host app events
   */
  private setupEventListeners() {
    // SYNC_USER listener - handles user login from host app
    this.eventListeners[JFC_CHAT_EVENTS.SYNC_USER] = ((event: CustomEvent<SyncUserPayload>) => {
      console.log('[ChatWidget] SYNC_USER received:', event.detail);
      this.handleSyncUser(event.detail);
    }) as EventListener;

    // LOGOUT_USER listener - handles user logout from host app
    this.eventListeners[JFC_CHAT_EVENTS.LOGOUT_USER] = ((event: CustomEvent<LogoutUserPayload>) => {
      console.log('[ChatWidget] LOGOUT_USER received:', event.detail);
      this.handleLogoutUser(event.detail);
    }) as EventListener;

    // CHANGE_LANGUAGE listener - handles language changes from host app
    this.eventListeners[JFC_CHAT_EVENTS.CHANGE_LANGUAGE] = ((event: CustomEvent<ChangeLanguagePayload>) => {
      console.log('[ChatWidget] CHANGE_LANGUAGE received:', event.detail);
      this.handleChangeLanguage(event.detail);
    }) as EventListener;

    // Register listeners
    window.addEventListener(JFC_CHAT_EVENTS.SYNC_USER, this.eventListeners[JFC_CHAT_EVENTS.SYNC_USER]);
    window.addEventListener(JFC_CHAT_EVENTS.LOGOUT_USER, this.eventListeners[JFC_CHAT_EVENTS.LOGOUT_USER]);
    window.addEventListener(JFC_CHAT_EVENTS.CHANGE_LANGUAGE, this.eventListeners[JFC_CHAT_EVENTS.CHANGE_LANGUAGE]);
  }

  /**
   * Remove event listeners
   */
  private removeEventListeners() {
    Object.entries(this.eventListeners).forEach(([eventName, listener]) => {
      window.removeEventListener(eventName, listener);
    });
    this.eventListeners = {};
  }

  /**
   * Handle SYNC_USER event - reinitialize with new user data
   */
  private handleSyncUser(payload: SyncUserPayload) {
    console.log('[ChatWidget] handleSyncUser: customerId=', payload.customerId, 'customerNo=', payload.customerNo, 'current groupID=', this.config.groupID);
    // OpenIM chat identity is email-derived. Include email in the account
    // comparison so logging into another email under the same customer/user id
    // does not reuse the previous SDK session.
    const isSameUser =
      getAccountKey(this.config.userID, this.config.userInfo.email) ===
      getAccountKey(payload.userid, payload.email);
    // groupID = customerId (the OpenIM groupID)
    const isGroupChange = this.config.groupID !== payload.customerId;

    // If same user and same group, keep config fresh but avoid creating a
    // container on pages where the host layout is not mounted.
    if (isSameUser && !isGroupChange) {
      this.config.userID = payload.userid;
      this.config.jfcToken = payload.jfcToken;
      this.config.userInfo = {
        type: toUserType(payload.userType),
        nickname: payload.customerName || payload.email,
        email: payload.email
      };
      this.config.groupID = payload.customerId;
      this.config.groupName = payload.customerName;
      this.config.customerNo = payload.customerNo;

      const currentContainer = document.getElementById(this.config.containerId!);
      if (currentContainer && currentContainer !== this.container) {
        this.render();
      }
      return;
    }

    // Update config with new user data
    this.config.userID = payload.userid;
    this.config.jfcToken = payload.jfcToken;
    this.config.userInfo = {
      type: toUserType(payload.userType),
      nickname: payload.customerName || payload.email,
      email: payload.email
    };
    // groupID = customerId (unique, used as OpenIM groupID)
    this.config.groupID = payload.customerId;
    this.config.groupName = payload.customerName;
    this.config.customerNo = payload.customerNo;

    if (isSameUser && isGroupChange) {
      // Same user, just group change - dispatch event for token update AND re-render with new props
      window.dispatchEvent(new CustomEvent('jfc-chat:group-change', {
        detail: {
          groupID: payload.customerId,
          groupName: payload.customerName,
          customerNo: payload.customerNo,
          jfcToken: payload.jfcToken,  // Include new token for API calls
          timestamp: Date.now()
        },
        bubbles: true,
        composed: true
      }));
      // Re-render to update React component props (Init useEffect will skip since same user)
      this.render();
    } else {
      // Different user - full re-render
      this.render();
    }
  }

  /**
   * Handle LOGOUT_USER event - cleanup and disconnect
   */
  private handleLogoutUser(_payload: LogoutUserPayload) {
    // Clear config
    this.config.userID = '';
    this.config.jfcToken = '';
    this.config.userInfo = { type: UserType.Customer, nickname: '' };
    this.config.groupID = '';
    this.config.groupName = '';
    this.config.customerNo = '';

    if (this.container) {
      this.container.style.display = 'none';
    }

    const cleanup = getChatSessionCleanup();
    const unmount = () => {
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
    };

    if (cleanup) {
      cleanup().finally(unmount);
    } else {
      unmount();
    }
  }

  /**
   * Handle CHANGE_LANGUAGE event - update widget language
   */
  private handleChangeLanguage(payload: ChangeLanguagePayload) {
    const newLang = payload.language?.toLowerCase().substring(0, 2) as SupportedLanguage;
    if (SUPPORTED_LANGUAGES.includes(newLang)) {
      this.config.language = newLang;
      // Re-render with new language
      this.render();
    }
  }

  /**
   * Render the chat widget
   */
  private render() {
    // Verify container is still in the DOM (Vue might have recreated it)
    const currentContainer = document.getElementById(this.config.containerId!);

    // If no container found, reuse the existing detached container when
    // returning from a host route that temporarily removed it.
    if (!currentContainer) {
      if (this.container && this.root) {
        if (!document.documentElement.contains(this.container)) {
          document.body.appendChild(this.container);
        }
      } else {
        const newContainer = document.createElement('div');
        newContainer.id = this.config.containerId!;
        document.body.appendChild(newContainer);
        this.container = newContainer;
        this.root = createRoot(newContainer);
      }
    } else if (currentContainer !== this.container) {
      // Vue can recreate the host placeholder during route changes. Preserve the
      // existing React root so same-account switches do not reset chat state.
      if (this.container && this.root) {
        try {
          currentContainer.replaceWith(this.container);
        } catch {
          this.root.unmount();
          this.container = currentContainer;
          this.root = createRoot(currentContainer);
        }
      } else {
        this.container = currentContainer;
        this.root = createRoot(currentContainer);
      }
    } else if (!this.root) {
      // Container exists but no root - create one
      this.container = currentContainer;
      this.root = createRoot(currentContainer);
    }

    // Ensure container is visible when rendering
    if (this.container) {
      this.container.style.display = 'block';
    }

    if (!this.root) {
      return;
    }

    // Remount only when the actual chat account changes. Customer/group switches
    // are handled inside ChatContext so the SDK session and membership state stay intact.
    const sessionKey = getAccountKey(this.config.userID, this.config.userInfo.email);

    this.root.render(
      <React.StrictMode>
        <LanguageProvider initialLanguage={this.config.language}>
          <ChatProvider key={sessionKey}>
            <ChatWidget
              userID={this.config.userID}
              jfcToken={this.config.jfcToken}
              userInfo={this.config.userInfo}
              groupID={this.config.groupID || ''}
              groupName={this.config.groupName || ''}
              customerNo={this.config.customerNo || ''}
              mode={this.config.mode || 'widget'}
            />
          </ChatProvider>
        </LanguageProvider>
      </React.StrictMode>
    );
  }

  /**
   * Setup trigger button to open chat
   */
  private setupTriggerButton() {
    const button = document.getElementById(this.config.triggerButtonId!);
    if (button) {
      button.addEventListener('click', () => {
        this.open();
      });
    }
  }

  /**
   * Open the chat widget
   */
  open() {
    if (this.container) {
      this.container.style.display = 'block';
      // Dispatch custom event for open
      window.dispatchEvent(new CustomEvent('jfc-chat-open'));
    }
  }

  /**
   * Close the chat widget
   */
  close() {
    if (this.container) {
      this.container.style.display = 'none';
      // Dispatch custom event for close
      window.dispatchEvent(new CustomEvent('jfc-chat-close'));
    }
  }

  /**
   * Toggle the chat widget
   */
  toggle() {
    if (this.container) {
      const isVisible = this.container.style.display !== 'none';
      if (isVisible) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  /**
   * Update user information
   */
  updateUser(userInfo: Partial<JFCChatWidgetConfig['userInfo']>) {
    this.config.userInfo = {
      ...this.config.userInfo,
      ...userInfo,
    };
    this.render();
  }

  /**
   * Refresh the widget after the host app recreates the target container.
   */
  refresh(config?: Partial<JFCChatWidgetConfig>) {
    if (config) {
      this.config = {
        ...this.config,
        ...config,
        userInfo: {
          ...this.config.userInfo,
          ...(config.userInfo || {}),
        },
      };
    }
    this.render();
  }

  /**
   * Destroy the widget and clean up
   */
  destroy() {
    // Remove event listeners first
    this.removeEventListeners();

    if (this.container) {
      this.container.style.display = 'none';
    }

    const cleanup = getChatSessionCleanup();
    const remove = () => {
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
      if (this.container) {
        this.container.remove();
        this.container = null;
      }
    };

    if (cleanup) {
      cleanup().finally(remove);
    } else {
      remove();
    }
  }
}

// Auto-initialize if config is provided in window
if (typeof window !== 'undefined') {
  // Expose to window for global access
  (window as any).JFCChatWidget = JFCChatWidget;

  // Auto-init if config is present
  const autoInitConfig = (window as any).jfcChatWidgetConfig;
  if (autoInitConfig && !(window as any)._jfcChatDisabled) {
    const widget = new JFCChatWidget(autoInitConfig);
    widget.init();
    (window as any).jfcChatWidget = widget;
  }
}

export default JFCChatWidget;
